import { LineItem } from "~graphql/sdk";
import { PickTypes } from "./types";

type Options = {
  separator?: string;
  objectType?: string;
};

export interface FormatSeatLocationParams extends PickTypes<
  LineItem,
  {
    seatZone: true;
    seatSection: true;
    seatLabel: true;
  }
> {}

export const formatSeatLocation = (
  seatZone: FormatSeatLocationParams["seatZone"],
  seatSection: FormatSeatLocationParams["seatSection"],
  seatLabel: FormatSeatLocationParams["seatLabel"],
  options: Options = {}
) => {
  const { objectType, separator = ", " } = options;

  // For general admission areas in seated events, just return the label
  if (objectType === "GeneralAdmissionArea") {
    return seatLabel;
  }

  // Parse the seat label to extract row and seat information
  const seatInfo = parseSeatLabel(seatLabel, separator);

  // Build the location parts
  const locationParts: string[] = [];

  // Add zone if present
  if (seatZone) {
    locationParts.push(seatZone);
  }

  // Add section if present
  if (seatSection) {
    locationParts.push(seatSection);
  }

  // Add seat information if present
  if (seatInfo) {
    locationParts.push(seatInfo);
  }

  // Join all parts with the separator
  return locationParts.join(separator);
};

/**
 * Parses a seat label string to extract row and seat information
 * @param seatLabel - The raw seat label (e.g., "A-1-2" or "B-5")
 * @param separator - The separator to use between row and seat
 * @returns Formatted seat information or null if invalid
 */
const parseSeatLabel = (
  seatLabel: string | null | undefined,
  separator: string
): string | null => {
  if (!seatLabel) return null;

  const parts = seatLabel.split("-");

  if (parts.length > 2) {
    // Format: "Zone-Row-Seat" -> "Row X, Seat Y"
    const row = parts[1].trim();
    const seat = parts[2].trim();
    return `Row ${row}${separator}Seat ${seat}`;
  } else if (parts.length === 2) {
    // Format: "Zone-Seat" -> "Seat Y"
    const seat = parts[1].trim();
    return `Seat ${seat}`;
  }

  return null;
};
